import { useEffect, useRef, useState } from 'react'
import {
  CloseButton,
  Input,
  Button,
  VStack,
  HStack,
  Alert,
  Text,
  InputGroup,
  IconButton,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCopy } from 'react-icons/fi'
import { useMintCanvasser } from '../../../hooks/useCampaign'
import { extractApiMessage } from '../../../lib/apiError'
import { publicOrigin } from '../../../lib/native/instance'
import { useFormatInstanceCode } from '../../../hooks/useInstanceCode'
import { ResponsiveModal } from '../../shared'
import type { MintCanvasserResult } from '../../../types/canvasserGrant'

// L11 slice 1e, Task 15: show-once credential modal, opened from CampaignCanvassersPage's "Add
// canvasser" button. Mirrors components/admin/IssueInviteModal.tsx's shape: a form gated on the
// mint mutation, replaced by a success view once it resolves. Unlike IssueInviteModal, the
// server returns TWO show-once credentials (a magic link and a short code) rather than one, so
// the success view has two copyable InputGroups instead of one.

interface MintCanvasserModalProps {
  campaignId: string
  isOpen: boolean
  onClose: () => void
  /** [L11-3b §4D] Self-service prefill — the acting staff member's own name. */
  initialFirstName?: string
  initialLastName?: string
  /** Header override; defaults to the organizer-facing "Add Canvasser". */
  title?: string
}

export function MintCanvasserModal({
  campaignId, isOpen, onClose, initialFirstName, initialLastName, title = 'Add Canvasser',
}: MintCanvasserModalProps) {
  const mintCanvasser = useMintCanvasser(campaignId)
  const formatCode = useFormatInstanceCode() // [Native B1, NB1-11] <orgId>-<CODE>

  const [firstName, setFirstName] = useState(initialFirstName ?? '')
  const [lastName, setLastName] = useState(initialLastName ?? '')
  const [phone, setPhone] = useState('')
  const [minted, setMinted] = useState<MintCanvasserResult | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const wasOpenRef = useRef(false)
  // Re-seed the name fields only on the closed→open transition (the modal stays mounted between
  // opens, so the useState initializers above run only once). Gating on the open EDGE — not on
  // isOpen being true on every render — is what guarantees a prefill that resolves late (e.g.
  // useProfile() landing while the modal is already open) can never overwrite what the user has
  // already typed.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setFirstName(initialFirstName ?? '')
      setLastName(initialLastName ?? '')
    }
    wasOpenRef.current = isOpen
  }, [isOpen, initialFirstName, initialLastName])

  const handleClose = () => {
    setFirstName(initialFirstName ?? '')
    setLastName(initialLastName ?? '')
    setPhone('')
    setMinted(null)
    setSubmitError(null)
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    try {
      const result = await mintCanvasser.mutateAsync({
        firstName,
        lastName,
        phone: phone || undefined,
      })
      setMinted(result)
    } catch (err: unknown) {
      setSubmitError(extractApiMessage(err, 'Failed to add canvasser. Please try again.'))
    }
  }

  // [Native B1] The instance's origin; null on native with no instance → no link, copy disabled.
  const origin = publicOrigin()
  const magicLink = minted && origin ? `${origin}${minted.magicLinkPath}` : ''

  const copyLink = () => {
    navigator.clipboard.writeText(magicLink)
    toaster.create({ type: 'success', title: 'Magic link copied' })
  }

  const copyCode = () => {
    if (!minted) return
    navigator.clipboard.writeText(formatCode(minted.shortCode))
    toaster.create({ type: 'success', title: 'Short code copied' })
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{title}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} /></Dialog.CloseTrigger>
          <Dialog.Body>
            {minted ? (
              <VStack gap={4} align="stretch">
                <Alert.Root status="success" borderRadius="md">
                  <Alert.Indicator />
                  Credentials for {minted.fullName} are shown only once -- copy them now.
                </Alert.Root>

                {minted.matchOutcome === 'Linked' && (
                  <Text fontSize="sm" color="muted">
                    Linked to the existing contact for {minted.fullName} — no duplicate was created.
                  </Text>
                )}
                {minted.matchOutcome === 'Ambiguous' && (
                  <Text fontSize="sm" color="warning.fg">
                    Possible duplicates exist for this name and number — a new contact was created
                    and nothing was linked. Merge them from the contact record if they are the same person.
                  </Text>
                )}

                <Field.Root>
                  <Field.Label>Magic link</Field.Label>
                  <InputGroup endElement={
                    <IconButton
                      aria-label="Copy link"
                      size="sm"
                      minH="44px"
                      variant="ghost"
                      disabled={!magicLink}
                      onClick={copyLink}><FiCopy /></IconButton>
                  }>
                    <Input size="sm" value={magicLink} readOnly />
                  </InputGroup>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Short code</Field.Label>
                  <InputGroup endElement={
                    <IconButton
                      aria-label="Copy code"
                      size="sm"
                      minH="44px"
                      variant="ghost"
                      onClick={copyCode}><FiCopy /></IconButton>
                  }>
                    <Input size="sm" value={formatCode(minted.shortCode)} readOnly />
                  </InputGroup>
                </Field.Root>

                <Text fontSize="sm" color="muted">
                  Code expires {new Date(minted.shortCodeExpiresAt).toLocaleString()}
                </Text>
                <Text fontSize="sm" color="muted">
                  Link stays valid until the canvasser is removed.
                </Text>
              </VStack>
            ) : (
              <VStack gap={4} align="stretch">
                {!!submitError && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    {submitError}
                  </Alert.Root>
                )}

                <Field.Root required>
                  <Field.Label>First name</Field.Label>
                  <Input
                    size="sm"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Field.Root>

                <Field.Root required>
                  <Field.Label>Last name</Field.Label>
                  <Input
                    size="sm"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>Phone</Field.Label>
                  <Input
                    size="sm"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Field.Root>
              </VStack>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <HStack gap={3}>
              {minted ? (
                <Button colorPalette="brand" size="sm" minH="44px" onClick={handleClose}>
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" minH="44px" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="brand"
                    size="sm"
                    minH="44px"
                    onClick={handleSubmit}
                    loading={mintCanvasser.isPending}
                    disabled={!firstName.trim() || !lastName.trim()}
                  >
                    Add canvasser
                  </Button>
                </>
              )}
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
